/**
 * The audit viewer's own API calls.
 *
 * Kept local to this feature rather than added to `@/lib/api` so that this
 * session's PR touches no file another wave-2 session also owns — the same
 * reasoning as the backend module boundary.
 */

import { ApiError, type ApiErrorBody } from '@/lib/api'

import type { AuditEventFilters, AuditEventList } from './types'

const BASE = '/api/v1/audit'

interface Page {
  limit: number
  offset: number
}

/**
 * A `datetime-local` input's value carries no offset — it is the browser's
 * local wall-clock time. `Date` parses that as local time (per the date-time
 * string grammar in the spec), so converting through it and back out with
 * `toISOString()` is what turns it into the UTC instant the API's tz-aware
 * `occurred_at` column actually needs, rather than sending an ambiguous
 * naive string the server would otherwise have to guess at.
 */
function toUtcIso(localDateTime: string): string | undefined {
  const parsed = new Date(localDateTime)
  return Number.isNaN(parsed.getTime()) ? undefined : parsed.toISOString()
}

function toQuery(filters: AuditEventFilters, page?: Page): string {
  const params = new URLSearchParams()
  if (filters.actor) params.set('actor', filters.actor)
  if (filters.action) params.set('action', filters.action)
  if (filters.since) {
    const since = toUtcIso(filters.since)
    if (since) params.set('since', since)
  }
  if (filters.until) {
    const until = toUtcIso(filters.until)
    if (until) params.set('until', until)
  }
  if (page) {
    params.set('limit', String(page.limit))
    params.set('offset', String(page.offset))
  }
  const query = params.toString()
  return query ? `?${query}` : ''
}

async function request<T>(path: string): Promise<T> {
  const response = await fetch(`${BASE}${path}`, { credentials: 'same-origin' })
  if (!response.ok) {
    let body: ApiErrorBody | undefined
    try {
      body = (await response.json()) as ApiErrorBody
    } catch {
      body = undefined
    }
    throw new ApiError(
      response.status,
      body?.error.code ?? 'temporary_failure',
      body?.error.message ?? response.statusText,
      body?.correlation_id,
    )
  }
  return (await response.json()) as T
}

export const auditApi = {
  events: (filters: AuditEventFilters, page: Page) =>
    request<AuditEventList>(`/events${toQuery(filters, page)}`),
  /** A plain URL: the browser downloads it directly, `Content-Disposition` and all. */
  exportUrl: (filters: AuditEventFilters) => `${BASE}/events/export${toQuery(filters)}`,
}
